import { useState, useCallback, useRef, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import {
  WorkcentreTableRow,
  updateConstrainingEndDate,
} from '../utils/planHelpers'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { useDataQuality } from './DataQuality.data'

interface ConstrainingHorizonsTableProps {
  data: WorkcentreTableRow[]
  onDataChange: (updatedData: WorkcentreTableRow[]) => void
  planStartWeek: Date
}

export const ConstrainingHorizonsTable = ({
  data,
  onDataChange,
  planStartWeek,
}: ConstrainingHorizonsTableProps) => {
  const [isLoading] = useState<boolean>(false)
  const [isSaving] = useState<boolean>(false)
  const [showDataQuality, setShowDataQuality] = useState<boolean>(false)
  const gridApiRef = useRef<any>(null)

  // Fetch data quality data
  const {
    data: dataQualityResult,
    isLoading: isDataQualityLoading,
    error: dataQualityError,
  } = useDataQuality()
  const processRow = useCallback(
    (newRow: WorkcentreTableRow, oldRow: WorkcentreTableRow) => {
      const updatedRow = { ...newRow }

      // Auto-calculate constrainingEndDate when constrainingHorizon changes
      if (newRow.constrainingHorizon !== oldRow.constrainingHorizon) {
        updatedRow.constrainingEndDate = updateConstrainingEndDate(
          planStartWeek,
          newRow.constrainingHorizon
        )
      }

      return updatedRow
    },
    [planStartWeek]
  )

  // Merge data with data quality information
  const mergedData = useMemo(() => {
    if (!showDataQuality || !dataQualityResult?.dataQualityMap) {
      return data
    }

    return data.map((row) => {
      const dataQualityInfo = dataQualityResult.dataQualityMap.get(row.code)
      if (dataQualityInfo) {
        return {
          ...row,
          ...dataQualityInfo,
          dataQualityWorkCentreKey: dataQualityInfo.workCentreKey,
        }
      }
      return row
    })
  }, [data, dataQualityResult, showDataQuality])

  const updateTableData = useCallback(
    (changedRows: Record<number, WorkcentreTableRow>) => {
      const rowsMap: Map<string, WorkcentreTableRow> =
        gridApiRef.current.getRowModels()
      const rowsArray: WorkcentreTableRow[] = rowsMap
        ? Array.from(rowsMap.values())
        : []
      onDataChange(rowsArray)
    },
    [data, onDataChange]
  )

  // Base columns (always visible)
  const baseColumns: ExtendedGridColDef[] = [
    {
      field: 'siteLabel',
      headerName: 'Site',
      width: 150,
      editable: false,
    },
    {
      field: 'code',
      headerName: 'Workcentre Code',
      width: 180,
      editable: false,
    },
    {
      field: 'constrainingHorizon',
      headerName: 'Constraining Horizon',
      width: 160,
      type: 'number',
      editable: true,
      valueParser: (value: string) => {
        const parsed = parseInt(value, 10)
        return isNaN(parsed) || parsed < 1 ? 1 : parsed
      },
    },
    {
      field: 'forwardFill',
      headerName: 'Forward Fill',
      width: 120,
      type: 'number',
      editable: true,
      valueParser: (value: string) => {
        const parsed = parseInt(value, 10)
        return isNaN(parsed) || parsed < 0 ? 0 : parsed
      },
    },
    {
      field: 'constrainingEndDate',
      headerName: 'Constraining End Date',
      width: 180,
      editable: false,
      valueFormatter: (value: Date) => {
        if (!value) return ''
        try {
          return value.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        } catch (e) {
          // value may not be a valid date object could be a string in the format "2025-10-19T23:00:00.000Z"
          const parsedDate = new Date(value)
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          }
          console.error('Error formatting date:', e, value)
          return ''
        }
      },
    },
  ]

  // Data quality columns (only visible when expanded)
  const dataQualityColumns: ExtendedGridColDef[] = [
    {
      field: 'forecastDate',
      headerName: 'Forecast Date',
      width: 140,
      editable: false,
      valueFormatter: (value: string) => {
        if (!value) return ''
        return new Date(value).toLocaleDateString('en-GB')
      },
    },
    {
      field: 'inventoryDate',
      headerName: 'Inventory Date',
      width: 140,
      editable: false,
      valueFormatter: (value: string) => {
        if (!value) return ''
        return new Date(value).toLocaleDateString('en-GB')
      },
    },
    {
      field: 'workCentreCapacity',
      headerName: 'Work Centre Capacity',
      width: 160,
      type: 'number',
      editable: false,
    },
    {
      field: 'numberOfProducts',
      headerName: 'Number of Products',
      width: 150,
      type: 'number',
      editable: false,
    },
    {
      field: 'missingChangeOvers',
      headerName: 'Missing Change Overs',
      width: 160,
      type: 'number',
      editable: false,
    },
    {
      field: 'invalidBatch',
      headerName: 'Invalid Batch',
      width: 120,
      type: 'number',
      editable: false,
    },
    {
      field: 'invalidSkus',
      headerName: 'Invalid SKUs',
      width: 120,
      type: 'number',
      editable: false,
    },
    {
      field: 'orphanProduction',
      headerName: 'Orphan Production',
      width: 140,
      type: 'number',
      editable: false,
    },
    {
      field: 'orphanSkus',
      headerName: 'Orphan SKUs',
      width: 120,
      type: 'number',
      editable: false,
    },
    {
      field: 'plannedProductionAmount',
      headerName: 'Planned Production Amount',
      width: 180,
      type: 'number',
      editable: false,
    },
    {
      field: 'stockInTransit',
      headerName: 'Stock in Transit',
      width: 140,
      type: 'number',
      editable: false,
    },
    {
      field: 'index',
      headerName: 'Index',
      width: 100,
      type: 'number',
      editable: false,
    },
  ]

  // Combine columns based on showDataQuality state
  const columns = useMemo(() => {
    return showDataQuality
      ? [...baseColumns, ...dataQualityColumns]
      : baseColumns
  }, [showDataQuality])

  const handleToggleDataQuality = () => {
    setShowDataQuality(!showDataQuality)
  }

  return (
    <Box sx={{ height: '100%', width: '100%', minWidth: 0, overflow: 'auto' }}>
      {/* Toggle Button */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={showDataQuality ? 'contained' : 'outlined'}
          onClick={handleToggleDataQuality}
          disabled={isDataQualityLoading}
          sx={{ minWidth: '200px' }}
        >
          {isDataQualityLoading
            ? 'Loading Data Quality...'
            : showDataQuality
              ? 'Hide Data Quality'
              : 'Expand Data Quality'}
        </Button>
      </Box>

      <BaseTable
        data={mergedData}
        columns={columns}
        processRow={processRow}
        updateTableData={updateTableData}
        isLoading={isLoading || (showDataQuality && isDataQualityLoading)}
        isSaving={isSaving}
        tableHeight={400}
        checkBoxSelection
        apiRefCallback={(apiRef) => {
          gridApiRef.current = apiRef.current
        }}
      />
    </Box>
  )
}
